import { faCube } from "@fortawesome/pro-light-svg-icons";
import { Box, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import ReactPlayer from "react-player";

import BgGrid from "@/assets/bg_grid.png";
import {
  Icon,
  Tooltip,
  TooltipTitleWithImage,
  Typography,
} from "@/components/ui-v2";
import { AudioPreviewTile } from "@/components/ui-v2/Tiles/AudioPreviewTile/AudioPreviewTile";
import { GuidanceFileParameters, InferenceParameters } from "@/types";

const PREVIEW_HEIGHT = 80;

const ReferenceFileContainer = styled(Box)({
  position: "relative",
  width: PREVIEW_HEIGHT,
  display: "flex",
  flexDirection: "column",
});

const ReferenceFileWrapper = styled(Box)({
  position: "relative",
  width: "100%",
  height: PREVIEW_HEIGHT,
  flexShrink: 0,
});

const ReferenceFileBackground = styled("div")(({ theme }) => ({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  backgroundColor: theme.palette.secondary[7],
}));

const GridBackground = styled("div")({
  width: "100%",
  height: "100%",
  backgroundPosition: "top",
  backgroundRepeat: "repeat",
  backgroundSize: "32px 32px",
  opacity: 0.7,
});

const StyledImage = styled("img")({
  position: "relative",
  zIndex: 10,
  objectFit: "contain",
  width: "100%",
  height: "100%",
});

const DetailsStrip = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  padding: `0 ${theme.spacing(1)}`,
  backgroundColor: theme.palette.secondary[6],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  borderBottomLeftRadius: 4,
  borderBottomRightRadius: 4,
  alignItems: "center",
}));

const getDetailsInfo = (
  file: GuidanceFileParameters,
  inferenceParameters: InferenceParameters,
) => {
  let detailsTitle = "Forge Ref.";
  let detailsSubtext = "";

  if (inferenceParameters?.upscaleRatio) {
    detailsTitle = `Upscaled ${inferenceParameters?.upscaleRatio}x`;
  } else {
    detailsSubtext = `${Math.round(file.weight * 100)}% similarity`;
    switch (file.type) {
      case "PROMPT":
        detailsTitle = "Style Ref.";
        break;
      case "SCRIBBLE":
        detailsTitle = "Sketch";
        break;
      case "COLOR_SKETCH":
        detailsTitle = "Sketch";
        break;
      case "DEPTH":
        detailsTitle = "Structure";
        break;
      case "POSE":
        detailsTitle = "Char. Pose";
        break;
      case "FACE":
        detailsTitle = "Char. Face";
        break;
      case "SOFTEDGE_HED":
        detailsTitle = "Soft Edge";
        break;
      case "CANNY":
        detailsTitle = "Outline";
        break;
      case "LINEART":
        detailsTitle = "Line Art";
        break;
      case "SEGMENTATION":
        detailsTitle = "Segmentation";
        break;
      case "FIRST_FRAME":
        detailsTitle = "First Frame";
        detailsSubtext = "";
        break;
      case "LAST_FRAME":
        detailsTitle = "Last Frame";
        detailsSubtext = "";
        break;
      case "EDITING_REFERENCE":
        detailsTitle = "Edit Ref.";
        detailsSubtext = "";
        break;
      case "INIT_VIDEO":
        detailsTitle = "Video Ref.";
        detailsSubtext = "";
        break;
      case "INIT_MESH":
        detailsTitle = "Mesh Ref.";
        detailsSubtext = "";
        break;
      case "AUDIO":
        detailsTitle = "Audio Track";
        detailsSubtext = "";
        break;
    }
  }

  return {
    detailsTitle,
    detailsSubtext,
  };
};

interface ReferenceFileProps {
  file: GuidanceFileParameters;
  inferenceParameters: InferenceParameters;
}

const ReferenceFile = (props: ReferenceFileProps) => {
  const { file, inferenceParameters } = props;

  const { detailsTitle, detailsSubtext } = getDetailsInfo(
    file,
    inferenceParameters,
  );
  let tooltipTitle = detailsTitle;
  if (detailsSubtext) {
    tooltipTitle += ` - ${detailsSubtext}`;
  }

  let content: JSX.Element | null = null;
  let tooltipContent: JSX.Element | null = null;
  switch (file.type) {
    case "INIT_VIDEO":
      content = (
        <ReactPlayer
          muted
          url={file.url}
          controls={false}
          width="100%"
          height="100%"
          config={{
            file: {
              attributes: {
                preload: "metadata",
              },
            },
          }}
        />
      );
      tooltipContent = (
        <ReactPlayer
          muted
          playing
          loop
          url={file.url}
          controls={false}
          width="100%"
          height="100%"
          config={{
            file: {
              attributes: {
                preload: "metadata",
              },
            },
          }}
        />
      );
      break;
    case "INIT_MESH":
      content = (
        <Stack
          height="64px"
          fontSize={32}
          justifyContent="center"
          direction="column"
          alignItems="center"
          gap={1}
        >
          {" "}
          <Icon size="xl" icon={faCube} />
        </Stack>
      );
      break;
    case "AUDIO":
      content = <AudioPreviewTile url={file.url} />;
      break;
    default:
      content = (
        <>
          <ReferenceFileBackground>
            <GridBackground
              sx={{
                backgroundImage: `url(${BgGrid})`,
              }}
            />
          </ReferenceFileBackground>
          <StyledImage src={file.url} draggable={false} alt="Preview" />
        </>
      );
      tooltipContent = (
        <TooltipTitleWithImage
          title={tooltipTitle}
          imageSrc={file.preprocessedUrl || file.url}
        />
      );
      break;
  }

  return (
    <Tooltip title={tooltipContent} placement="left" arrow>
      <ReferenceFileContainer>
        <ReferenceFileWrapper>{content}</ReferenceFileWrapper>
        <DetailsStrip>
          <Typography color="secondary.1" variant="mini">
            {detailsTitle}
          </Typography>
          <Typography color="secondary.1" variant="micro">
            {detailsSubtext}
          </Typography>
        </DetailsStrip>
      </ReferenceFileContainer>
    </Tooltip>
  );
};

const StyledGrid = styled("div")({
  width: "100%",
  display: "grid",
  gridTemplateColumns: `repeat(auto-fill, minmax(${PREVIEW_HEIGHT}px, 1fr))`,
  gridAutoFlow: "row",
  gridAutoColumns: "max-content",
});

interface ReferenceFilesProps {
  files: GuidanceFileParameters[];
  inferenceParameters: InferenceParameters;
}

export const ReferenceFiles = (props: ReferenceFilesProps) => {
  const { files, inferenceParameters } = props;

  return (
    <StyledGrid>
      {files.map((file, ix) => (
        <ReferenceFile
          key={`sidebar-reference-${ix}`}
          file={file}
          inferenceParameters={inferenceParameters}
        />
      ))}
    </StyledGrid>
  );
};
